import { useMemo, useState } from "react";
import { editsMasterData, quantityAt } from "@lagerlog/core";
import type { Booking, CountResult, Inventory } from "@lagerlog/core";
import { containerWords, euro, findArticles } from "../catalog.ts";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import { Named, fmt, fmtDiff, unitsOf } from "./Book.tsx";
import { useWide } from "../wide.ts";
import { Parts } from "./Stock.tsx";
import type { Article } from "../catalog.ts";

/** What a count is entered in by default on this device: single containers ("bottle"), or a pack by its plural („Kästen à 20“). */
const UNIT_KEY = "lagerlog.countUnit";

function readUnit(): string {
  try {
    const v = localStorage.getItem(UNIT_KEY) ?? "bottle";
    // Stored before as the size of a carton of wine: "6".
    return /^\d+$/.test(v) ? `Kartons à ${v}` : v;
  } catch {
    return "bottle";
  }
}

/** Single containers, short: „Fl.“, „Fässer“. */
const single = (a: Article) => a.units[0]!.plural ?? a.units[0]!.name;

/**
 * The choices for counting: single containers of every kind there (bottles, cans, kegs), then
 * each pack one of the articles has, smallest first. Only what the articles have is offered.
 */
export function countUnits(articles: Article[]): { value: string; label: string }[] {
  const singles = [...new Set(articles.map((a) => containerWords(a).many))];
  const packs = new Map<string, number>();
  for (const a of articles) for (const u of a.units.slice(1)) if (!packs.has(u.plural ?? u.name)) packs.set(u.plural ?? u.name, u.factor / a.bottle);
  return [
    { value: "bottle", label: singles.length ? singles.join(", ") : "Flaschen" },
    ...[...packs.entries()].sort((x, y) => x[1] - y[1] || x[0].localeCompare(y[0])).map(([name]) => ({ value: name, label: name })),
  ];
}

/** The unit a row starts with: the chosen pack if the article has it, else a single container. */
export function defaultFactor(a: Article, choice: string): number {
  const pack = choice === "bottle" ? undefined : a.units.find((u, i) => i > 0 && (u.plural ?? u.name) === choice);
  return pack?.factor ?? a.bottle;
}

const time = (at: number) => new Date(at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/**
 * Counting a location (I1, simplified): today's counts of all devices are the inventory. Stored are
 * the counts, never the differences; those are computed and change when late bookings arrive.
 * What nobody counted today stays "nicht gezählt", which is not the same as counted 0.
 */
export function Count() {
  const { store, version } = useStore();
  const catalog = useCatalog();
  const drinks = useDrinks();
  const ledger = useLedger();
  // From the inventory: "#/zaehlen?ort=<location>" opens that location.
  const [location, setLocation] = useState(() => new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("ort") ?? "");
  const [values, setValues] = useState<Record<string, string>>({});
  const [units, setUnits] = useState<Record<string, number>>({});
  /** Loose bottles next to the cases: "5 Kartons à 6 + 3 Fl." */
  const [loose, setLoose] = useState<Record<string, string>>({});
  const [choice, setChoice] = useState(readUnit);
  const [extra, setExtra] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [chosenBlind, setBlind] = useState(true);
  // Staff count blind, always (B2): neither the stock in the books nor, after saving, the differences.
  const keeper = editsMasterData(store.rights().role);
  const blind = chosenBlind || !keeper;
  const wide = useWide();

  const scope = useMemo(() => new Set(store.config?.lagers ?? []), [store.config]);
  const locations = useMemo(() => (catalog ? [...catalog.locations.values()].filter((l) => scope.has(l.lager)) : []), [catalog, scope]);

  /** The open inventory, if there is one, by storage. */
  const running = useMemo(() => {
    const out = new Map<string, Inventory>();
    for (const inv of catalog?.inventories.values() ?? []) if (inv.status === "open") for (const l of inv.lagers) out.set(l, inv);
    return out;
  }, [catalog]);
  const openInventory = [...running.values()][0];

  /**
   * Per location: the articles to count and the latest count of each: of the open inventory if
   * the storage is in one, else today's (a quick count, I5).
   */
  const lists = useMemo(() => {
    const out = new Map<string, { articles: Set<string>; counted: Map<string, CountResult> }>();
    if (!ledger) return out;
    const at = (loc: string) => {
      let list = out.get(loc);
      if (!list) out.set(loc, (list = { articles: new Set(), counted: new Map() }));
      return list;
    };
    for (const acc of ledger.accounts.values()) if (acc.qty !== 0) at(acc.location).articles.add(acc.article);
    for (const d of ledger.deficits.values()) if (d.qty > 0) at(d.location).articles.add(d.article);
    const today = new Date().setHours(0, 0, 0, 0);
    for (const r of ledger.counts.values()) {
      const inv = running.get(r.lager);
      if (inv ? r.inventory !== inv.id : r.at < today) continue;
      const list = at(r.location);
      list.articles.add(r.article);
      const seen = list.counted.get(r.article);
      if (!seen || seen.at <= r.at) list.counted.set(r.article, r);
    }
    return out;
    // The replica appends to its state in place: the version says when it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger, version, running]);

  if (!catalog || !ledger) return null;
  const loc = catalog.locations.get(location);
  const inventory = loc ? running.get(loc.lager) : undefined;
  const list = lists.get(location);
  const counted = list?.counted ?? new Map<string, CountResult>();
  const articles = [...new Set([...(list?.articles ?? []), ...extra])]
    .map((id) => catalog.articles.get(id))
    .filter((a) => a !== undefined)
    // Not yet counted first, then the counted ones; each part by name.
    .sort((a, b) => Number(counted.has(a.id)) - Number(counted.has(b.id)) || a.name.localeCompare(b.name));
  // A choice stored on this device that the articles here do not have stays offered, so the select shows it.
  const choices = countUnits(articles);
  if (!choices.some((u) => u.value === choice)) choices.push({ value: choice, label: choice });
  const results = [...counted.values()].sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || a.article.localeCompare(b.article));
  const differing = results.filter((r) => r.diff !== 0);
  const progress = (id: string) => {
    const l = lists.get(id);
    return l && l.articles.size > 0 ? ` · ${l.counted.size}/${l.articles.size}` : "";
  };

  async function save() {
    if (!loc) return;
    for (const article of new Set([...Object.keys(values), ...Object.keys(loose)])) {
      const raw = values[article] ?? "";
      const extra = loose[article] ?? "";
      if (raw.trim() === "" && extra.trim() === "") continue;
      const a = catalog!.articles.get(article);
      const factor = units[article] ?? (a ? defaultFactor(a, choice) : 1);
      const bottle = a ? defaultFactor(a, "bottle") : 1;
      const num = (v: string) => (v.trim() === "" ? 0 : Number(v.replace(",", ".")));
      // "1,5" bottles of gin are 1050 ml; whole base units in the end. Loose bottles only next to cases.
      const qty = Math.max(0, Math.round(num(raw) * factor + (factor !== bottle ? num(extra) * bottle : 0)));
      if (!Number.isFinite(qty)) continue;
      await store.book({ kind: "count", article, lager: loc.lager, location: loc.id, qty, ...(inventory ? { inventory: inventory.id } : {}) } as Booking);
    }
    setValues({});
    setUnits({});
    setLoose({});
  }

  // Every hit up to 40, as in Buchen: a producer with many wines must not hide the one sought.
  const matches = query.length >= 2 ? findArticles(catalog.articles.values(), query, 40) : [];

  const unitSelect = (a: Article) => (
    <select
      className="unit"
      value={units[a.id] ?? defaultFactor(a, choice)}
      onChange={(e) => setUnits({ ...units, [a.id]: Number(e.target.value) })}
      aria-label="Einheit"
      data-testid={`count-unit-${a.id}`}
    >
      {unitsOf(a).map((u) => (
        <option key={u.factor} value={u.factor}>
          {u.name}
        </option>
      ))}
    </select>
  );
  const input = (a: Article) => (
    <input className="narrow" inputMode="decimal" value={values[a.id] ?? ""} onChange={(e) => setValues({ ...values, [a.id]: e.target.value })} data-testid={`count-${a.id}`} />
  );
  /** Cases and loose bottles: the second line only when a case is chosen; it keeps its place otherwise, so rows stay the same height. */
  const isCases = (a: Article) => (units[a.id] ?? defaultFactor(a, choice)) !== defaultFactor(a, "bottle");
  const entry = (a: Article, anyCases: boolean) => {
    const cases = isCases(a);
    return (
      <span className="count-input">
        <span className="count-line">
          {input(a)}
          {unitSelect(a)}
        </span>
        <span className={`count-line loose${cases ? "" : anyCases ? " off" : " gone"}`} aria-hidden={!cases}>
          <input
            className="narrow"
            inputMode="numeric"
            value={loose[a.id] ?? ""}
            onChange={(e) => setLoose({ ...loose, [a.id]: e.target.value })}
            tabIndex={cases ? 0 : -1}
            aria-label={`einzelne ${containerWords(a).many}`}
            data-testid={`count-loose-${a.id}`}
          />
          <span className="unit-word">{single(a)} dazu</span>
        </span>
      </span>
    );
  };
  const who = (c: CountResult) => {
    // Own counts not yet confirmed by the server are not in the replica's map.
    const booking = store.replica?.bookings.get(c.id);
    return store.personName(booking ? booking.person : store.config?.userId) ?? booking?.device ?? store.config?.device;
  };

  const head = (
    <>
      {openInventory && !keeper ? (
        <div className="card inventory-banner" data-testid="inventory-banner">
          <strong>{openInventory.name} läuft</strong>
          <span className="muted">{openInventory.lagers.map((l) => catalog.lagers.get(l)?.name ?? l).join(", ")} · Zählungen dort gehören zur Inventur</span>
        </div>
      ) : openInventory ? (
        <a className="card inventory-banner" href="#/inventur" data-testid="inventory-banner">
          <strong>{openInventory.name} läuft</strong>
          <span className="muted">
            {openInventory.lagers.map((l) => catalog.lagers.get(l)?.name ?? l).join(", ")} · Zählungen dort gehören zur Inventur · Differenzen und Abschluss →
          </span>
        </a>
      ) : (
        <p className="row between">
          <span className="muted">{keeper ? "Schnell zählen, oder eine Inventur über ganze Lager:" : "Zählen, was am Lagerort steht:"}</span>
          {keeper && (
            <a href="#/inventur" data-testid="inventory-new">
              Inventur …
            </a>
          )}
        </p>
      )}
      <label>
        Lagerort
        <select
          value={location}
          onChange={(e) => {
            setLocation(e.target.value);
            setValues({});
            setLoose({});
            setExtra([]);
            const l = catalog.locations.get(e.target.value);
            const inv = l ? running.get(l.lager) : undefined;
            if (inv) setBlind(inv.blind);
          }}
          data-testid="count-location"
        >
          <option value="">bitte wählen</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {catalog.lagers.get(l.lager)?.name} · {l.name}
              {progress(l.id)}
            </option>
          ))}
        </select>
      </label>
      {loc && (
        <>
          <div className="row between">
            <strong data-testid="count-progress">
              {counted.size} von {articles.length} gezählt
            </strong>
            {keeper && (
              <label className="check">
                <input type="checkbox" checked={blind} onChange={(e) => setBlind(e.target.checked)} data-testid="blind-toggle" />
                blind
              </label>
            )}
          </div>
          <label className="row count-default">
            <span className="muted">Zählen in</span>
            <select
              value={choice}
              onChange={(e) => {
                // Rows already filled in keep the unit they were entered in; empty rows follow the new default.
                const kept: Record<string, number> = {};
                for (const [id, raw] of Object.entries(values)) {
                  const a = catalog.articles.get(id);
                  if ((raw.trim() !== "" || (loose[id] ?? "").trim() !== "") && a) kept[id] = units[id] ?? defaultFactor(a, choice);
                }
                setUnits(kept);
                setChoice(e.target.value);
                try {
                  localStorage.setItem(UNIT_KEY, e.target.value);
                } catch {
                  // the choice holds until the app closes
                }
              }}
              data-testid="count-default-unit"
            >
              {choices.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </label>
          <p className="muted">Leer lassen heißt „nicht gezählt“, nicht 0.</p>
        </>
      )}
    </>
  );

  const more = loc && (
    <div className="count-footer">
      <input placeholder="Weiteren Artikel suchen" value={query} onChange={(e) => setQuery(e.target.value)} />
      {matches.map((a) => (
        <button key={a.id} className="link" onClick={() => [setExtra([...extra, a.id]), setQuery("")]}>
          + {a.name}
        </button>
      ))}
      <button className="primary big" onClick={save} data-testid="count-save">
        Zählung speichern
      </button>
    </div>
  );

  const differences = loc && results.length > 0 && !keeper ? (
    <section>
      <p className="muted" data-testid="count-saved">Gespeichert. Die Differenzen sehen Inhaber und Verwaltung.</p>
    </section>
  ) : loc && results.length > 0 && (
    <section>
      <h3>{inventory ? "Differenzen dieser Inventur" : "Differenzen heute"}</h3>
      {differing.length === 0 && <p className="muted">Alles Gezählte stimmt mit dem Journal überein.</p>}
      <ul className="list">
        {differing.map((r) => (
          <li key={r.id} className={`row between ${r.diff < 0 ? "negative" : ""}`}>
            <span>{catalog.articles.get(r.article)?.name}</span>
            <span className="side" data-testid={`diff-${r.article}`}>
              <span>
                {fmtDiff(catalog.articles.get(r.article), r.diff)}
              </span>
              {store.prices && <span>{euro(r.value)}</span>}
            </span>
          </li>
        ))}
      </ul>
      {differing.length > 1 && store.prices && (
        <p className="row between">
          <strong>Summe</strong>
          <strong className="side" data-testid="diff-sum">
            {euro(differing.reduce((s, r) => s + r.value, 0))}
          </strong>
        </p>
      )}
    </section>
  );

  if (wide) {
    return (
      <div className="split count wide-left">
        <div className="pane">
          {head}
          {loc && (
            <table className="grid count-table">
              <thead>
                <tr>
                  <th>{drinks ? "Getränk" : "Wein"}</th>
                  <th>Gezählt</th>
                  <th>Zuletzt gezählt</th>
                  {!blind && <th>laut Journal</th>}
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => {
                  const c = counted.get(a.id);
                  return (
                    <tr key={a.id}>
                      <td>
                        <Named a={a} />
                      </td>
                      <td>
                        {entry(a, articles.some(isCases))}
                      </td>
                      <td className="stack-cell">
                        {c ? (
                          <>
                            <span className="counted">
                              <Parts a={a} qty={c.counted} />
                            </span>
                            <span className="muted">
                              {time(c.at)} · {who(c)}
                            </span>
                          </>
                        ) : (
                          <span className="muted">nicht gezählt</span>
                        )}
                      </td>
                      {!blind && (
                        <td className="stack-cell">
                          <Parts a={a} qty={quantityAt(ledger, a.id, loc.id)} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {more}
        </div>
        <div className="pane side-pane">
          {differences || (
            <div className="card placeholder">
              <p>{loc ? "Nach dem Speichern stehen hier die Differenzen des Tages, in Flaschen und Euro, mit Summe." : "Einen Lagerort wählen. Zählen können mehrere Geräte gleichzeitig; zusammen ergibt das die Zählung des Tages."}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="count">
      {head}
      {loc && (
        <ul className="list">
          {articles.map((a) => {
            const c = counted.get(a.id);
            const device = c && who(c);
            return (
              <li key={a.id} className="row between">
                <span>
                  {a.name}
                  <br />
                  {c ? (
                    <span className="counted">
                      gezählt {fmt(a, c.counted)} · {time(c.at)}
                      {device ? ` · ${device}` : ""}
                    </span>
                  ) : (
                    <span className="muted">nicht gezählt</span>
                  )}
                  {!blind && <span className="muted"> · laut Journal {fmt(a, quantityAt(ledger, a.id, loc.id))}</span>}
                </span>
                {entry(a, articles.some(isCases))}
              </li>
            );
          })}
        </ul>
      )}
      {more}
      {differences}
    </div>
  );
}
